import classNames from "classnames";
import React, {
    HTMLAttributes, ReactNode, useContext,
} from "react";
import Styles from "./Settings.module.scss";
import { SettingsSectionContext } from "./base";
import { Permission } from "../../../__generated__/api";

export interface SettingsSectionProps extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    title: string;
    description: string;
    descriptionElement?: ReactNode;
    permissions: Permission[];
    isVisible?: boolean;
    isOptional?: boolean;
    isFullWidth?: boolean;
    isHideTitle?: boolean;
    isLoading?: boolean;
    containerClassName?: string;
}

const SettingsSection = React.forwardRef((
    args: SettingsSectionProps,
    inputRef: React.ForwardedRef<HTMLDivElement>,
): ReactNode => {
    const {
        children,
        title,
        description,
        descriptionElement,
        className,
        containerClassName,
        permissions,
        isVisible,
        isOptional,
        isFullWidth,
        isHideTitle,
        isLoading,
        ...rest
    } = args;

    const { reference, canDisplay } = useContext(SettingsSectionContext).useSetting(
        inputRef,
        {
            permissions: permissions ?? [],
            title,
            description,
            isVisible,
        },
    );

    if (!canDisplay) {
        return null;
    }

    return (
        <div className={classNames(Styles.setting, containerClassName)} ref={reference} {...rest}>

            {!isFullWidth && !isHideTitle && (
                <div className={Styles["settings-description"]}>
                    <h4>
                        {title}
                        {isOptional && <small className={Styles["settings-optional"]}>(optional)</small>}
                    </h4>
                    <p>
                        {descriptionElement ?? description}
                    </p>
                </div>
            )}

            <div className={classNames(
                Styles["settings-value"],
                isFullWidth ? Styles["settings-value-full-width"] : "",
                isHideTitle ? Styles["settings-value-hide-title"] : "",
                className,
            )}
            >
                {isLoading ? (
                    <div className="loading">
                        <div className={Styles.loading}>
                            {"⌛".repeat(Math.random() * 15)}
                            {" "}
                            Loading...
                        </div>
                    </div>
                ) : (
                    children
                )}
            </div>

        </div>
    );
});

SettingsSection.defaultProps = {
    isOptional: false,
    isFullWidth: false,
    descriptionElement: null,
    isHideTitle: false,
    isLoading: false,
    containerClassName: "",
};

const PermissionSets = {
    Administrator: [Permission.Administrator],
    GroupAdministrator: [Permission.Administrator, Permission.GroupAdministrator],
    Everyone: [],
};

type SettingsSectionWithTypes = typeof SettingsSection & {
    Permissions: typeof PermissionSets;
    PickerClassName: string;
    BadgeListClassName: string;
}

const Settings = SettingsSection as unknown as SettingsSectionWithTypes;
Settings.Permissions = PermissionSets;
Settings.PickerClassName = Styles["select-picker"];
Settings.BadgeListClassName = Styles["badge-list"];

export default Settings;
